/**
 * Shared semantic-tone palette used across the app for status pills, dots,
 * tags, and nav cards. Consumers pick a tone (e.g. `success`, `warning`)
 * and look up a class string in one of the maps below. To add `animate-pulse`
 * for live states (e.g. running task), append it at the call site rather
 * than baking it into the palette.
 */
export type Accent =
  'neutral' | 'cyan' | 'teal' | 'violet' | 'success' | 'warning' | 'danger'

/** Solid dot with glow halo. Append `animate-pulse` for live states. */
export const ACCENT_DOT: Record<Accent, string> = {
  neutral: 'bg-text-mute',
  cyan: 'bg-accent shadow-[0_0_6px_rgb(var(--accent))]',
  teal: 'bg-accent-2 shadow-[0_0_6px_rgb(var(--accent-2))]',
  violet: 'bg-violet shadow-[0_0_6px_rgb(var(--violet))]',
  success: 'bg-success shadow-[0_0_6px_rgb(var(--success))]',
  warning: 'bg-warning shadow-[0_0_6px_rgb(var(--warning))]',
  danger: 'bg-danger shadow-[0_0_6px_rgb(var(--danger))]',
}

/** Pill / tag style: soft background + matching text + tinted border. */
export const ACCENT_PILL: Record<Accent, string> = {
  neutral: 'bg-panel-2 text-text-dim border-border',
  cyan: 'bg-accent-soft text-accent border-accent-tint',
  teal: 'bg-accent-2/10 text-accent-2 border-accent-2/30',
  violet: 'bg-violet-soft text-violet border-violet/30',
  success: 'bg-success-soft text-success border-success/30',
  warning: 'bg-warning-soft text-warning border-warning/30',
  danger: 'bg-danger-soft text-danger border-danger/30',
}

/* --- NavCard-specific maps (left bar, value text, active/hover bg) --- */

export const ACCENT_BAR: Record<Accent, string> = {
  neutral: 'before:bg-border-strong',
  cyan: 'before:bg-accent',
  teal: 'before:bg-accent-2',
  violet: 'before:bg-violet',
  success: 'before:bg-success',
  warning: 'before:bg-warning',
  danger: 'before:bg-danger',
}

export const ACCENT_VALUE: Record<Accent, string> = {
  neutral: 'text-text',
  cyan: 'text-accent',
  teal: 'text-accent-2',
  violet: 'text-violet',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
}

export const ACCENT_BG_ACTIVE: Record<Accent, string> = {
  neutral: 'bg-panel-2',
  cyan: 'bg-accent/10',
  teal: 'bg-accent-2/10',
  violet: 'bg-violet/10',
  success: 'bg-success/10',
  warning: 'bg-warning/10',
  danger: 'bg-danger/10',
}

export const ACCENT_BG_HOVER: Record<Accent, string> = {
  neutral: 'hover:bg-panel-2/60',
  cyan: 'hover:bg-accent/5',
  teal: 'hover:bg-accent-2/5',
  violet: 'hover:bg-violet/5',
  success: 'hover:bg-success/5',
  warning: 'hover:bg-warning/5',
  danger: 'hover:bg-danger/5',
}
